import React, { useEffect, useRef, useState } from "react";
import consts from "shared/consts";
import {
  ContextMenuOption,
  createMenuOption,
  createMenuSeparator,
  MenuOption,
} from "frontend/context-menu/optionsContextMenu";
import { Html } from "react-konva-utils";
import Konva from "konva";
import tracking from "frontend/tracking";
import style from "frontend/context-menu/optionsContextMenu.module.css";
import mystyle from "./context-menu-element.module.css";
import cn from "classnames";
import { Floater } from "frontend/ui-components/floaters/floater";
import useOutsideRef from "frontend/utils/click-outside-handler";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import { getPathPrefix } from "../utils/getPathPrefix";
import { GanttAddTaskIcon } from "elements/gantt/icons/gantt-add-task-icon";
import { useGanttMaxTasks } from "elements/gantt/hooks/gantt-max-tasks";
import { Predicate, countByPredicates } from "shared/util/fn-utils";
import { useDynamicElementActions } from "./element-dynamic-data/hooks/use-dynamic-element-actions";
import { cordEnabled } from "frontend/hooks/use-cord";
import { PlanFeatureName } from "shared/datamodel/schemas";
import { isElementLinkBadgeSupporting } from "elements/bridge";
import { isElementInGantt } from "./elements/canvas-elements-utils";

export type ContextMenuOps =
  | "copy"
  | "cut"
  | "delete"
  | "tofront"
  | "toback"
  | "forward"
  | "backward"
  | "lock"
  | "unlock"
  | "group"
  | "ungroup"
  | "paste"
  | "zoom-in"
  | "zoom-out"
  | "add-text"
  | "add-note"
  | "add-shape"
  | "add-comment"
  | "add-mindmap"
  | "link"
  | "set-as-thumbnail"
  | "export-frame"
  | "export-frame-image"
  | "export-frame-pdf"
  | "clear-thumbnail"
  | "create-monday-item"
  | "create-monday-subitem"
  | "add-gantt-task"
  | "show-data-layer-editor";

const icons = {
  copy: getPathPrefix("/images/element-context-menu/copy.svg"),
  cut: getPathPrefix("/images/element-context-menu/cut.svg"),
  delete: getPathPrefix("/images/element-context-menu/trashcan.svg"),
  link: getPathPrefix("/images/element-context-menu/link-icon.svg"),
  tofront: getPathPrefix("/images/element-context-menu/send-to-front.svg"),
  forward: getPathPrefix("/images/element-context-menu/move-forward.svg"),
  backward: getPathPrefix("/images/element-context-menu/move-backward.svg"),
  toback: getPathPrefix("/images/element-context-menu/send-to-back.svg"),
  lock: getPathPrefix("/images/element-context-menu/lock.svg"),
  unlock: getPathPrefix("/images/element-context-menu/unlock.svg"),
  group: getPathPrefix("/images/element-context-menu/group.svg"),
  ungroup: getPathPrefix("/images/element-context-menu/ungroup.svg"),

  paste: getPathPrefix("/images/element-context-menu/paste.svg"),
  zoomIn: getPathPrefix("/images/element-context-menu/zoom-in.svg"),
  zoomOut: getPathPrefix("/images/element-context-menu/zoom-out.svg"),
  addText: getPathPrefix("/images/element-context-menu/add-text.svg"),
  addSticky: getPathPrefix("/images/element-context-menu/add-sticky.svg"),
  addShape: getPathPrefix("/images/element-context-menu/add-shape.svg"),
  addComment: getPathPrefix("/images/element-context-menu/add-comment.svg"),
  addMindmap: getPathPrefix("/images/element-context-menu/add-mindmap.svg"),

  createMondayItem: getPathPrefix("/images/monday-mini-logo.svg"),

  createMondaySubmenuItem: getPathPrefix("/images/element-context-menu/create-monday-item-icon.svg"),
  createMondaySubmenuSubitem: getPathPrefix("/images/element-context-menu/create-monday-subitem-icon.svg"),

  setAsThumbnail: getPathPrefix("/images/element-context-menu/set-as-thumbnail.svg"),
  export: getPathPrefix("/images/element-context-menu/export.svg"),
  exportImage: getPathPrefix("/images/element-context-menu/export-image.svg"),
  exportPdf: getPathPrefix("/images/element-context-menu/export-pdf.svg"),
};

const nodePredicates: Record<string, Predicate<Konva.Node>> = {
  locked: (node) => (node.attrs.element.lock ?? 0) != 0,
  isFrame: (node) => node.attrs.isFrame,
} as const;

export interface ContextMenuProps {
  anchor: DOMRectReadOnly;
  nodes?: Iterable<Konva.Node>;
  enablePaste: boolean;
  enableGroup: boolean;
  enableUngroup: boolean;
  enableComments: boolean;
  enableRelativeMove: boolean;
  theme?: "light" | "dark";
  onItemSelection: (e: ContextMenuOps | null, requiresUpgrade: boolean) => void;
  margin?: number;
  thumbnailId?: string | null;
  allowPdfExport?: boolean;
  openedFrom?: "toolbar" | "canvas";
  newStyle?: boolean;
}

export default function ContextMenuElement({
  anchor,
  onItemSelection,
  nodes,
  enablePaste,
  enableGroup,
  enableUngroup,
  enableComments,
  enableRelativeMove,
  margin,
  thumbnailId,
  allowPdfExport,
  newStyle,
}: ContextMenuProps) {
  const maxAllowedTasksInPlan = useGanttMaxTasks();
  const premiumExportEnabled = usePlanFeatureValue(PlanFeatureName.PREMIUM_EXPORT);
  const { getDynamicElementForSelection } = useDynamicElementActions();

  useEffect(() => {
    tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "context-menu-shown");
  }, []);

  const [selectedMenuOption, setSelectedMenuOption] = useState<string | undefined>(undefined);

  function onClick(e: MouseEvent) {
    const id = (e.currentTarget as HTMLElement).dataset.id as ContextMenuOps;
    const upgrade = (e.currentTarget as HTMLElement).dataset.upgrade;
    console.log("onclick", id, upgrade);
    onItemSelection(id, !!upgrade);
    if (id) {
      tracking.trackEvent(consts.TRACKING_CATEGORY.CANVAS_ACTION, "context-menu-action", id);
    }
  }

  const nodesArray = [...(nodes ?? [])];

  // To know what options to show in the context menu we need to check a few things about our elements.
  // Look at the criteria object `elementPredicates` to see what we check for.
  const count = countByPredicates(nodePredicates, nodesArray);
  const totalElements = nodesArray.length;

  const allLocked = count.locked === totalElements;

  const allFrames = count.isFrame === totalElements;
  const isSingleFrame = allFrames && totalElements === 1;

  const linkable = totalElements === 1 && isElementLinkBadgeSupporting(nodesArray[0].attrs.type);
  const haveLink = linkable && !!nodesArray[0].attrs.element.link;

  const isCurrentThumbnail = isSingleFrame && nodesArray[0].attrs.id === thumbnailId;
  const ganttId = nodesArray.find((node) => node.attrs.type === "gantt")?.attrs.id;

  // Check if any selected element is a task inside a Gantt chart
  const isTaskInsideGantt = nodesArray.some((node) =>
    isElementInGantt({ ...node.attrs.element, type: node.attrs.type }, true)
  );

  const blockUngroupForDataLayer = getDynamicElementForSelection(nodesArray) !== null;

  const menuItemsForElements = [
    createMenuOption({
      title: consts.CONTEXT_MENU_ELEMENT.COPY,
      iconPath: icons.copy,
      id: "copy",
      onClick: (event) => {
        window.dispatchEvent(new ClipboardEvent("copy"));
        onClick(event);
      },
    }),
    // Cut option is not supported for tasks inside Gantt
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.CUT,
        iconPath: icons.cut,
        id: "cut",
        onClick: (event) => {
          window.dispatchEvent(new ClipboardEvent("cut"));
          onClick(event);
        },
        disabled: allLocked,
      }),
    createMenuOption({ title: consts.CONTEXT_MENU_ELEMENT.DELETE, iconPath: icons.delete, id: "delete", onClick }),
    // Separator and Link to option are not supported for tasks inside Gantt
    !isTaskInsideGantt && createMenuSeparator(),
    !isTaskInsideGantt &&
      createMenuOption({
        title: haveLink ? consts.CONTEXT_MENU_ELEMENT.EDIT_LINK : consts.CONTEXT_MENU_ELEMENT.ADD_LINK,
        iconPath: icons.link,
        id: "link",
        onClick,
        disabled: !linkable,
      }),
    !isTaskInsideGantt && createMenuSeparator(),
    // Lock/Unlock options are not supported for tasks inside Gantt
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.LOCK,
        iconPath: icons.lock,
        id: "lock",
        onClick,
        disabled: allLocked,
      }),
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.UNLOCK,
        iconPath: icons.unlock,
        id: "unlock",
        onClick,
        disabled: !allLocked,
      }),
    !isTaskInsideGantt && createMenuSeparator(),
    // Move to front/back options are not supported for tasks inside Gantt
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.MOVE_TO_FRONT,
        iconPath: icons.tofront,
        id: "tofront",
        onClick,
        disabled: allLocked || allFrames,
      }),
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.MOVE_FORWARD,
        icon: <img src={icons.forward} width="19" height="19" style={{ marginLeft: "-2px" }} />,
        id: "forward",
        onClick,
        disabled: !enableRelativeMove || allLocked || allFrames,
      }),
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.MOVE_BACKWARD,
        icon: <img src={icons.backward} width="19" height="19" style={{ marginLeft: "-2px" }} />,
        id: "backward",
        onClick,
        disabled: !enableRelativeMove || allLocked || allFrames,
      }),
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.MOVE_TO_BACK,
        iconPath: icons.toback,
        id: "toback",
        onClick,
        disabled: allLocked || allFrames,
      }),
    // Group/Ungroup options are not supported for tasks inside Gantt
    !isTaskInsideGantt && createMenuSeparator(),
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.GROUP,
        iconPath: icons.group,
        id: "group",
        onClick,
        disabled: !enableGroup || allLocked || allFrames,
      }),
    !isTaskInsideGantt &&
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.UNGROUP,
        iconPath: icons.ungroup,
        id: "ungroup",
        onClick,
        disabled: !enableUngroup || allLocked || allFrames || blockUngroupForDataLayer,
      }),
  ].flat();

  if (ganttId) {
    menuItemsForElements.push(createMenuSeparator());
    menuItemsForElements.push(
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.ADD_GANTT_TASK,
        iconPath: <GanttAddTaskIcon color="#fff" />,
        id: "add-gantt-task",
        onClick,
        disabled: parseInt(sessionStorage.getItem(ganttId) || "0") >= maxAllowedTasksInPlan,
      })
    );
  }

  if (isCurrentThumbnail) {
    menuItemsForElements.push(
      createMenuOption({
        title: consts.CONTEXT_MENU_ELEMENT.CLEAR_THUMBNAIL,
        iconPath: icons.setAsThumbnail,
        id: "clear-thumbnail",
        onClick,
      })
    );
  } else if (allFrames) {
    menuItemsForElements.push(createMenuSeparator());
    if (isSingleFrame) {
      menuItemsForElements.push(
        createMenuOption({
          title: consts.CONTEXT_MENU_ELEMENT.SET_AS_THUMBNAIL,
          iconPath: icons.setAsThumbnail,
          id: "set-as-thumbnail",
          onClick,
        })
      );
    }

    if (allowPdfExport) {
      menuItemsForElements.push(
        createMenuOption({
          title: consts.CONTEXT_MENU_ELEMENT.EXPORT,
          iconPath: icons.export,
          id: "export-frame",
          onClick: () => setSelectedMenuOption((selected) => (selected == "export-frame" ? undefined : "export-frame")),
          submenuItems: [
            createMenuOption({
              title: consts.CONTEXT_MENU_ELEMENT.EXPORT_PDF,
              id: "export-frame-pdf",
              iconPath: icons.exportPdf,
              onClick,
              requireUpgrade: !premiumExportEnabled,
            }),
            createMenuOption({
              title: consts.CONTEXT_MENU_ELEMENT.EXPORT_IMAGE,
              id: "export-frame-image",
              iconPath: icons.exportImage,
              onClick,
            }),
          ],
        })
      );
    } else {
      menuItemsForElements.push(
        createMenuOption({
          title: consts.CONTEXT_MENU_ELEMENT.EXPORT,
          id: "export-frame-image",
          iconPath: icons.export,
          onClick,
        })
      );
    }
  }

  const menuItemsForStage = [
    createMenuOption({
      title: consts.CONTEXT_MENU_STAGE.PASTE,
      iconPath: icons.paste,
      id: "paste",
      onClick: (event) => {
        window.dispatchEvent(new ClipboardEvent("paste"));
        onClick(event);
      },
      disabled: !enablePaste,
    }),
    createMenuSeparator(),
    createMenuOption({ title: consts.CONTEXT_MENU_STAGE.ZOOM_IN, iconPath: icons.zoomIn, id: "zoom-in", onClick }),
    createMenuOption({ title: consts.CONTEXT_MENU_STAGE.ZOOM_OUT, iconPath: icons.zoomOut, id: "zoom-out", onClick }),
    createMenuSeparator(),
    createMenuOption({ title: consts.CONTEXT_MENU_STAGE.ADD_TEXT, iconPath: icons.addText, id: "add-text", onClick }),
    createMenuOption({
      title: consts.CONTEXT_MENU_STAGE.ADD_STICKY,
      iconPath: icons.addSticky,
      id: "add-note",
      onClick,
    }),
    createMenuOption({
      title: consts.CONTEXT_MENU_STAGE.ADD_SHAPE,
      iconPath: icons.addShape,
      id: "add-shape",
      onClick,
    }),
    // createMenuOption(consts.CONTEXT_MENU_STAGE.ADD_MINDMAP, icons.addMindmap, "add-mindmap", onClick),

    cordEnabled() &&
      createMenuOption({
        title: consts.CONTEXT_MENU_STAGE.ADD_COMMENT,
        iconPath: icons.addComment,
        id: "add-comment",
        onClick,
        disabled: !enableComments,
      }),
  ];

  const items = nodes ? menuItemsForElements : menuItemsForStage;
  const filteredItems = items.filter((item) => !!item) as ContextMenuOption[];

  return newStyle ? (
    <ContextMenuViewCIM
      margin={margin}
      anchor={anchor}
      items={filteredItems}
      selectedMenuOption={selectedMenuOption}
      onDismiss={() => onItemSelection(null, false)}
    />
  ) : (
    <ContextMenuViewLegacy
      margin={margin}
      anchor={anchor}
      items={filteredItems}
      selectedMenuOption={selectedMenuOption}
      onDismiss={() => onItemSelection(null, false)}
    />
  );
}

interface ContextMenuViewProps {
  margin?: number;
  anchor: DOMRectReadOnly;
  items: ContextMenuOption[];
  selectedMenuOption: string | undefined;
  onDismiss: () => void;
}

const ContextMenuViewLegacy: React.FC<ContextMenuViewProps> = ({
  margin = 0,
  anchor,
  items,
  selectedMenuOption,
  onDismiss,
}) => {
  const ref = useRef<any>(null);
  useOutsideRef(ref, () => onDismiss(), { event: "click" });

  return (
    <Html transform={false}>
      <Floater
        ref={ref}
        side="bottom"
        arrowSize={0}
        margin={margin}
        relativeTo={anchor}
        className={cn(style.container, style.light)}
      >
        <div className={style.body} style={{ paddingTop: "8px" }} onContextMenu={(e) => e.preventDefault()}>
          {items.map((item, index) =>
            item.isSeparator ? (
              <hr key={index} className={style.separator} />
            ) : (
              <MenuOption key={index} option={item} isSelected={selectedMenuOption === item.id} />
            )
          )}
        </div>
      </Floater>
    </Html>
  );
};

function getIconForMenuOption(item: ContextMenuOption) {
  let icon = null;
  if (item.icon) {
    icon = item.icon;
  } else if (item.iconPath) {
    if (typeof item.iconPath === "object") {
      icon = item.iconPath;
    } else {
      icon = <img src={item.iconPath.toString()} width="17" height="17" />;
    }
  }
  return icon;
}

const ContextMenuViewCIM: React.FC<ContextMenuViewProps> = ({
  margin = 0,
  anchor,
  items,
  selectedMenuOption,
  onDismiss,
}) => {
  const ref = useRef<any>(null);
  const selectedRef = useRef<any>(null);
  useOutsideRef(ref, () => onDismiss(), { event: "click" });

  function renderSubMenu(relativeTo: any, side: any, items: ContextMenuOption[] | undefined) {
    if (!items) {
      return null;
    }
    return (
      <Floater side={side} arrowSize={0} margin={10} relativeTo={relativeTo} className={mystyle.ctxMenu}>
        <div onContextMenu={(e) => e.preventDefault()}>
          {items.map((item, index) => {
            if (item.isSeparator) {
              return <hr key={index} />;
            } else {
              return (
                <button
                  key={item.id}
                  data-testid={item.id}
                  data-id={item.id}
                  onClick={item.disabled ? undefined : item.onClick}
                  className={mystyle["ctxMenu-btn"]}
                  disabled={item.disabled}
                >
                  {getIconForMenuOption(item)}
                  <div>{item.title}</div>
                </button>
              );
            }
          })}
        </div>
      </Floater>
    );
  }

  return (
    <Html transform={false}>
      <Floater ref={ref} side="top" arrowSize={0} margin={margin} relativeTo={anchor} className={mystyle.ctxMenu}>
        <div onContextMenu={(e) => e.preventDefault()}>
          {items.map((item, index) =>
            item.isSeparator ? (
              <hr key={index} />
            ) : (
              <button
                key={item.id}
                ref={selectedMenuOption === item.id ? selectedRef : undefined}
                data-testid={item.id}
                data-id={item.id}
                onClick={item.disabled ? undefined : item.onClick}
                className={mystyle["ctxMenu-btn"]}
                disabled={item.disabled}
              >
                {getIconForMenuOption(item)}
                <div>{item.title}</div>
              </button>
            )
          )}
        </div>
      </Floater>
      {!!selectedMenuOption &&
        renderSubMenu(selectedRef, "right", items.find(({ id }) => id === selectedMenuOption)?.submenuItems)}
    </Html>
  );
};
